import React, { useState, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useWindowSize from 'react-use/lib/useWindowSize';

import WinXP from 'WinXP';
import LoadingScreen from './LoadingScreen';

const scanline = keyframes`
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100vh); }
`;

const MobileScreen = styled.div`
  position: fixed;
  inset: 0;
  background-color: #000;
  font-family: 'Courier New', 'Courier', 'Lucida Console', monospace;
  overflow: hidden;
`;

const MobileVideo = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 90%;
  height: 90%;
  transform: translate(-50%, -50%);
  object-fit: contain;
  z-index: 0;
  pointer-events: none;
`;

const MobileScanline = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.03) 50%,
    transparent 100%
  );
  animation: ${scanline} 3s linear infinite;
  pointer-events: none;
`;

const MobileContent = styled.div`
  position: absolute;
  top: 75%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0 24px;
  box-sizing: border-box;
`;

const MobileContinueBox = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.65);
  padding: 1.25rem 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileCTA = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: 1;
  transition: opacity 150ms ease-out;
  &::before {
    content: '▶ ';
  }
  &:active {
    opacity: 0.7;
  }
`;

function fallbackAfterShareError(url, setHint) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(url)
      .then(() => setHint('Link copied — paste on your computer.'))
      .catch(() =>
        setHint(
          'Could not share or copy. Try copying the URL from the address bar.',
        ),
      );
  } else {
    setHint(
      'Could not open share sheet. Try copying the URL from the address bar.',
    );
  }
}

function MobileGate() {
  const [hint, setHint] = useState(null);

  const sendToComputer = useCallback(() => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;

    const payload = {
      title: 'Bermuda',
      text: 'Open this on your computer.',
      url,
    };

    if (typeof navigator !== 'undefined' && navigator.share) {
      const tryShare = data => {
        if (
          typeof navigator.canShare === 'function' &&
          !navigator.canShare(data)
        ) {
          return Promise.reject(new Error('not_shareable'));
        }
        return navigator.share(data);
      };

      tryShare(payload)
        .then(() => setHint(null))
        .catch(err => {
          if (err && err.name === 'AbortError') return;
          if (err && err.message === 'not_shareable') {
            tryShare({ url })
              .then(() => setHint(null))
              .catch(err2 => {
                if (err2 && err2.name === 'AbortError') return;
                fallbackAfterShareError(url, setHint);
              });
            return;
          }
          fallbackAfterShareError(url, setHint);
        });
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => setHint('Link copied — paste on your computer.'))
        .catch(() =>
          setHint(
            'Could not copy automatically. Copy the URL from the address bar.',
          ),
        );
      return;
    }

    setHint(
      'Copy the address from your browser’s bar and open it on a computer.',
    );
  }, []);

  useEffect(() => {
    if (!hint) return undefined;
    const t = window.setTimeout(() => setHint(null), 3500);
    return () => window.clearTimeout(t);
  }, [hint]);

  return (
    <MobileScreen>
      <MobileVideo autoPlay loop muted playsInline src="/bermuda-enter.mp4" />
      <MobileScanline />
      <MobileContent>
        <MobileContinueBox>
          <MobileCTA onClick={sendToComputer}>Send to my computer</MobileCTA>
        </MobileContinueBox>
        {hint && (
          <p
            style={{
              color: '#aaa',
              fontSize: 12,
              lineHeight: 1.5,
              textAlign: 'center',
              maxWidth: 320,
              margin: 0,
            }}
          >
            {hint}
          </p>
        )}
      </MobileContent>
    </MobileScreen>
  );
}

const App = () => {
  const [loading, setLoading] = useState(true);
  const { width } = useWindowSize();
  const isMobile = width < 768;

  if (isMobile) {
    return <MobileGate />;
  }

  return (
    <>
      {loading && <LoadingScreen onEnter={() => setLoading(false)} />}
      {!loading && <WinXP />}
    </>
  );
};

export default App;
